import { Airport } from "@/models/airport";

// Letters that do not split into a plain letter plus an accent.
const PLAIN_LETTERS: Record<string, string> = {
  ø: "o",
  ł: "l",
  đ: "d",
  ð: "d",
  ı: "i",
  ß: "ss",
  æ: "ae",
  œ: "oe",
  þ: "th",
};

// Lower case with accents removed, so "sao paulo" finds "São Paulo" and
// "lodz" finds "Łódź".
const plain = (text: string): string =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[øłđðıßæœþ]/g, (letter) => PLAIN_LETTERS[letter]);

// Matches what the player types against the airport's IATA code, city,
// airport name and country, so "ath", "athens" and "gree" all find Athens.
// Better matches come first; ties go to the busier airport.
// Stand-in for the real fuzzy matcher: swap the body of this function for it;
// the UI only relies on the signature.
export const searchAirports = (
  term: string,
  airports: Airport[],
  limit = 20
): Airport[] => {
  const query = plain(term.trim());
  if (!query) return [];

  const rank = (airport: Airport): number => {
    const iata = plain(airport.iata);
    const city = plain(airport.city);
    const name = plain(airport.name);
    const country = plain(airport.country_name);
    if (iata === query) return 0;
    // A city and a country rank the same, so "gree" puts Athens (Greece)
    // ahead of small airports in cities like Greensboro: the busier wins.
    if (city.startsWith(query) || country.startsWith(query)) return 1;
    if (name.startsWith(query)) return 2;
    if (iata.startsWith(query)) return 3;
    if (city.includes(query) || name.includes(query) || country.includes(query)) {
      return 4;
    }
    return -1;
  };

  return airports
    .map((airport) => ({ airport, score: rank(airport) }))
    .filter(({ score }) => score >= 0)
    .sort(
      (a, b) =>
        a.score - b.score ||
        b.airport.daily_departures - a.airport.daily_departures
    )
    .slice(0, limit)
    .map(({ airport }) => airport);
};
